/**
 * AgentTasksManager — admin list & lifecycle view for agent tasks.
 *
 * ─── History ────────────────────────────────────────────────────────
 * The original component embedded a giant modal used for CREATE / EDIT.
 * That modal has been replaced by a full page (`AgentTaskEditor.jsx`)
 * routed at:
 *   • /manage/cloudgm-business/agent-tasks/new
 *   • /manage/cloudgm-business/agent-tasks/edit/:id
 *
 * This file now only handles:
 *   • Listing tasks (grid of cards)
 *   • Card actions: toggle active/draft · view agents · edit · delete
 *   • Navigating to the full-page editor for create/edit
 *   • Delete confirmation dialog
 */
import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  Plus, Loader2, RefreshCw, Pencil, Trash2,
  ClipboardList, Percent, AlertCircle, ChevronLeft, Users,
  Bot, ListTodo, Power,
} from 'lucide-react';
import { toast } from 'sonner';
import { getAuthHeaders } from '../../../utils/authHeaders';
import TaskAgentsView from './TaskAgentsView';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGES = {
  active:   { ar: 'فعّالة', en: 'Active',   cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  draft:    { ar: 'مسوّدة', en: 'Draft',    cls: 'bg-amber-500/15  text-amber-300  border-amber-500/30' },
  archived: { ar: 'مؤرشفة', en: 'Archived', cls: 'bg-white/5       text-white/40   border-white/10' },
};

export default function AgentTasksManager({ language = 'ar', mode = 'add', setActiveTab }) {
  const isAr = language === 'ar';
  const navigate = useNavigate();

  const [tasks, setTasks]                 = useState([]);
  const [loading, setLoading]             = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [viewingTask, setViewingTask]     = useState(null);
  const [togglingId, setTogglingId]       = useState(null);

  const goToEditor = (taskId) => {
    if (taskId) navigate(`/manage/cloudgm-business/agent-tasks/edit/${taskId}`);
    else        navigate('/manage/cloudgm-business/agent-tasks/new');
  };

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/agent-tasks`, { headers: getAuthHeaders() });
      setTasks(res.data?.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'فشل تحميل المهام' : 'Failed to load tasks'));
    } finally {
      setLoading(false);
    }
  }, [isAr]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const toggleActive = async (task) => {
    setTogglingId(task.id);
    try {
      const res = await axios.patch(
        `${API_URL}/api/admin/agent-tasks/${task.id}/toggle`,
        {},
        { headers: getAuthHeaders() },
      );
      const newStatus = res.data?.status;
      setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, status: newStatus } : t)));
      toast.success(newStatus === 'active'
        ? (isAr ? 'تم تفعيل المهمة — أصبحت ظاهرة للوكلاء' : 'Task enabled — now visible to agents')
        : (isAr ? 'تم إيقاف المهمة — لن تظهر للوكلاء' : 'Task disabled — hidden from agents'));
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'فشل التبديل' : 'Toggle failed'));
    } finally {
      setTogglingId(null);
    }
  };

  const deleteTask = async (task) => {
    try {
      await axios.delete(`${API_URL}/api/admin/agent-tasks/${task.id}`, { headers: getAuthHeaders() });
      toast.success(isAr ? '✓ تم الحذف' : '✓ Deleted');
      setConfirmDelete(null);
      fetchTasks();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'فشل الحذف' : 'Delete failed'));
    }
  };

  if (viewingTask) {
    return (
      <TaskAgentsView
        taskId={viewingTask}
        language={language}
        onBack={() => { setViewingTask(null); fetchTasks(); }}
      />
    );
  }

  return (
    <div data-testid="agent-tasks-manager">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          {setActiveTab && (
            <button
              onClick={() => setActiveTab('agent-tasks')}
              className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/60 hover:text-white"
              data-testid="agent-tasks-back"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          )}
          <ClipboardList className="h-6 w-6 text-orange-400" />
          <h2 className="text-xl font-bold text-white">
            {mode === 'follow'
              ? (isAr ? 'متابعة مهام الوكلاء' : 'Follow Agent Tasks')
              : (isAr ? 'إدراج مهام للوكلاء' : 'Add Agent Tasks')}
            <span className="ms-2 text-sm font-normal text-[#808080]">({tasks.length})</span>
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchTasks}
            disabled={loading}
            className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm hover:bg-white/10 flex items-center gap-2"
            data-testid="agent-tasks-refresh"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            {isAr ? 'تحديث' : 'Refresh'}
          </button>
          {mode !== 'follow' && (
            <button
              onClick={() => goToEditor(null)}
              className="px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium flex items-center gap-2"
              data-testid="agent-tasks-add-btn"
            >
              <Plus className="h-4 w-4" />
              {isAr ? 'إضافة مهمة' : 'Add Task'}
            </button>
          )}
        </div>
      </div>

      {/* List */}
      {loading && tasks.length === 0 ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-32 bg-white/[0.02] rounded-2xl animate-pulse" />
          ))}
        </div>
      ) : tasks.length === 0 ? (
        <div className="text-center py-16 bg-white/[0.02] rounded-2xl border border-white/10">
          <ClipboardList className="h-16 w-16 mx-auto mb-4 opacity-30 text-orange-400" />
          <p className="text-lg text-white">{isAr ? 'لا توجد مهام بعد' : 'No tasks yet'}</p>
          <p className="text-sm mt-2 text-[#606060]">
            {isAr ? 'أضف مهمة جديدة لتظهر للوكلاء فوراً' : 'Add a task to make it visible to agents'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {tasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              isAr={isAr}
              togglingId={togglingId}
              onToggle={toggleActive}
              onViewAgents={setViewingTask}
              onEdit={goToEditor}
              onDelete={setConfirmDelete}
            />
          ))}
        </div>
      )}

      {/* Delete confirmation */}
      {createPortal(
        <AnimatePresence>
          {confirmDelete && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
              onClick={() => setConfirmDelete(null)}
            >
              <motion.div
                initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-[#0a0a14] border border-red-500/40 rounded-2xl p-6 max-w-md w-full"
                data-testid="agent-task-confirm-delete"
              >
                <div className="flex items-center gap-3 mb-4">
                  <AlertCircle className="h-6 w-6 text-red-400" />
                  <h3 className="text-lg font-bold text-white">{isAr ? 'تأكيد الحذف' : 'Confirm Delete'}</h3>
                </div>
                <p className="text-white/80 text-sm mb-2">
                  {isAr ? 'هل أنت متأكد من حذف هذه المهمة؟' : 'Are you sure you want to delete this task?'}
                </p>
                <p className="text-white text-sm font-semibold mb-5">
                  &quot;{confirmDelete.title_ar || confirmDelete.title_en}&quot;
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setConfirmDelete(null)}
                    className="flex-1 py-2 rounded-lg bg-white/5 border border-white/10 text-white hover:bg-white/10"
                  >
                    {isAr ? 'إلغاء' : 'Cancel'}
                  </button>
                  <button
                    onClick={() => deleteTask(confirmDelete)}
                    className="flex-1 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white font-medium"
                    data-testid="agent-task-confirm-delete-btn"
                  >
                    {isAr ? 'حذف نهائي' : 'Delete'}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </div>
  );
}

/* ─────────────────────── Card ─────────────────────── */

function TaskCard({ task, isAr, togglingId, onToggle, onViewAgents, onEdit, onDelete }) {
  const badge = STATUS_BADGES[task.status] || STATUS_BADGES.active;

  // Prefer new tiers; fall back to legacy single-percentage schema.
  let tiers = Array.isArray(task.profit_tiers) ? task.profit_tiers : [];
  if (tiers.length === 0 && task.profit_percentage != null) {
    tiers = [{
      percentage: task.profit_percentage,
      description_ar: task.profit_details || '',
      description_en: '',
    }];
  }
  const taskMode = task.mode || (task.uses_ai_agent ? 'ai' : 'ai');

  return (
    <div
      className="p-5 bg-white/[0.02] rounded-2xl border border-white/10 hover:border-orange-500/40 transition-all"
      data-testid={`agent-task-card-${task.id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-white text-base truncate">{task.title_ar || task.title_en}</h3>
          {task.title_en && task.title_ar && (
            <p className="text-xs text-white/40 truncate">{task.title_en}</p>
          )}
        </div>
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${badge.cls}`}>
          {isAr ? badge.ar : badge.en}
        </span>
      </div>

      {(task.description_ar || task.description_en) && (
        <p className="text-sm text-white/70 mb-3 line-clamp-2">
          {isAr ? task.description_ar : task.description_en}
        </p>
      )}

      <div className="space-y-1.5 mb-3">
        {tiers.map((tier, i) => (
          <div
            key={i}
            className="flex items-center gap-2 px-2.5 py-1.5 bg-emerald-500/5 border border-emerald-500/20 rounded-lg text-xs"
            data-testid={`agent-task-tier-${task.id}-${i}`}
          >
            <span className="px-2 py-0.5 rounded-md bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 font-bold flex items-center gap-1 flex-shrink-0">
              <Percent className="h-2.5 w-2.5" />
              {tier.percentage}%
            </span>
            {(tier.description_ar || tier.description_en) && (
              <span className="text-white/80 truncate flex-1">
                {isAr
                  ? tier.description_ar || tier.description_en
                  : tier.description_en || tier.description_ar}
              </span>
            )}
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-white/5">
        <div className="flex items-center gap-3">
          <p className="text-[10px] text-white/30 font-mono">
            {task.created_at ? new Date(task.created_at).toLocaleDateString(isAr ? 'ar-SA' : 'en-US') : ''}
          </p>
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold border ${
              taskMode === 'ai'
                ? 'bg-cyan-500/10 border-cyan-500/30 text-cyan-300'
                : 'bg-violet-500/10 border-violet-500/30 text-violet-300'
            }`}
            data-testid={`agent-task-mode-${task.id}`}
          >
            {taskMode === 'ai' ? <Bot className="h-2.5 w-2.5" /> : <ListTodo className="h-2.5 w-2.5" />}
            {taskMode === 'ai' ? (isAr ? 'محادثة AI' : 'AI Chat') : (isAr ? 'نموذج' : 'Form')}
          </span>
        </div>

        <div className="flex items-center gap-1.5">
          <button
            onClick={() => onToggle(task)}
            disabled={togglingId === task.id}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition ${
              task.status === 'active'
                ? 'bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 border border-emerald-500/30'
                : 'bg-white/5 text-white/50 hover:bg-white/10 border border-white/10'
            }`}
            data-testid={`agent-task-toggle-${task.id}`}
            title={isAr
              ? (task.status === 'active' ? 'إيقاف — إخفاء عن الوكلاء' : 'تشغيل — إظهار للوكلاء')
              : (task.status === 'active' ? 'Disable' : 'Enable')}
          >
            {togglingId === task.id
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <Power className="h-3.5 w-3.5" />}
            {task.status === 'active' ? (isAr ? 'شغّالة' : 'On') : (isAr ? 'موقفة' : 'Off')}
          </button>
          <button
            onClick={() => onViewAgents(task.id)}
            className="px-2.5 py-1.5 rounded-md bg-orange-500/10 text-orange-300 hover:bg-orange-500/20 text-xs font-medium flex items-center gap-1.5"
            data-testid={`agent-task-view-agents-${task.id}`}
            title={isAr ? 'الوكلاء والنسب' : 'Agents & rates'}
          >
            <Users className="h-3.5 w-3.5" />
            {isAr ? 'الوكلاء' : 'Agents'}
          </button>
          <button
            onClick={() => onEdit(task.id)}
            className="p-1.5 rounded-md bg-blue-500/10 text-blue-300 hover:bg-blue-500/20"
            data-testid={`agent-task-edit-${task.id}`}
            title={isAr ? 'تعديل' : 'Edit'}
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => onDelete(task)}
            className="p-1.5 rounded-md bg-red-500/10 text-red-300 hover:bg-red-500/20"
            data-testid={`agent-task-delete-${task.id}`}
            title={isAr ? 'حذف' : 'Delete'}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
